import { useState, useEffect, useRef } from 'react';
import { setAttemptCounterCallback, clearAttemptCounterCallback } from '@/hooks/tgs2025/potionmaker/use-ai-potion-ui-bridge';

interface AttemptCounterProps {
  className?: string;
}

export const AttemptCounter = ({ className = '' }: AttemptCounterProps) => {
  const [currentAttempt, setCurrentAttempt] = useState(1); // Start at 1 since game begins immediately
  const [maxAttempts] = useState(3);
  const [ingredientCount, setIngredientCount] = useState(0);
  const svgRef = useRef<HTMLDivElement>(null);

  // Update SVG potion fills based on current attempt
  useEffect(() => {
    console.log(`🧪 AttemptCounter: Updating SVG for attempt ${currentAttempt}`);
    if (svgRef.current) {
      const svg = svgRef.current.querySelector('svg');
      if (svg) {
        // Reset all potions to transparent
        const potions = svg.querySelectorAll('#potion-1, #potion-2, #potion-3');
        console.log(`🧪 Found ${potions.length} potion elements`);
        potions.forEach((potion, index) => {
          console.log(`🧪 Removing 'filled' from potion-${index + 1}`);
          potion.classList.remove('filled');
        });

        // Fill potions up to current attempt
        console.log(`🧪 Filling potions 1 through ${currentAttempt}`);
        for (let i = 1; i <= currentAttempt; i++) {
          const potion = svg.querySelector(`#potion-${i}`);
          if (potion) {
            console.log(`🧪 Adding 'filled' to potion-${i}`);
            potion.classList.add('filled');
          } else {
            console.error(`🧪 Could not find potion-${i} element`);
          }
        }
        
        // Debug: Log current state of all potions
        potions.forEach((potion, index) => {
          const isFilled = potion.classList.contains('filled');
          console.log(`🧪 Potion-${index + 1}: ${isFilled ? 'FILLED' : 'empty'}`);
        });
      }
    }
  }, [currentAttempt]);

  // Register callback with UI Bridge for direct updates
  useEffect(() => {
    const handleAttemptUpdate = (attempt: number, _maxAttempts: number) => {
      console.log(`🎯 AttemptCounter: Received update - attempt ${attempt}/${_maxAttempts}`);
      setCurrentAttempt(attempt);
      setIngredientCount(0); // Reset ingredient count for new attempt
    };
    
    setAttemptCounterCallback(handleAttemptUpdate);
    
    return () => {
      clearAttemptCounterCallback();
    };
  }, []); // Empty dependency array - only register once

  // Always render since game starts immediately
  return (
    <div className={`flex flex-col items-center space-y-3 ${className}`}>
      <div className="text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg">
        Attempt
      </div>
      
      <div ref={svgRef} className="w-16 h-auto">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56.4 320.26">
          <defs>
            <style>
              {`
                .cls-1 {
                  fill: transparent;
                  stroke: #fff;
                  stroke-miterlimit: 10;
                  stroke-width: 2px;
                  transition: fill 0.3s ease;
                }

                .cls-1.filled {
                  fill: #fff;
                }

                .cls-2 {
                  fill: #231f20;
                  stroke: none;
                }

                /* Remove any focus outlines */
                svg * {
                  outline: none !important;
                }
                
                svg *:focus {
                  outline: none !important;
                }
              `}
            </style>
          </defs>
          <g id="Layer_2" data-name="Layer 2">
            <g id="potion-attempt">
              <g id="potion-attempt-array">
                <g id="attempt1">
                  <path id="potion-1" className="cls-1" d="M28.26,1c11.35.07,8.8,3.24,8.87,3.76s-.93,4.64-1.28,6.08a.49.49,0,0,0,.35.59c.76.19,2.2.64,2.63,1.46a4.16,4.16,0,0,1-1.48,4.66S36,17.11,35.88,27s0,17.11,8.4,23.3S54.46,64.16,54.46,64.16,60.06,84.36,44,93.8l-.89,2.36s-3.83,3.1-13.42,3.1h-3c-9.58,0-13.42-3.1-13.42-3.1l-.88-2.36C-3.66,84.36,2,64.16,2,64.16s1.77-7.67,10.17-13.87S20.68,36.87,20.53,27s-1.48-9.44-1.48-9.44a4.18,4.18,0,0,1-1.48-4.66c.43-.82,1.88-1.27,2.63-1.46a.48.48,0,0,0,.35-.59c-.34-1.44-1.34-5.65-1.28-6.08S16.8,1.07,28.14,1Z"/>
                  <g id="number1">
                    <path className="cls-2" d="M23,70.47a.64.64,0,0,1-.74.08.67.67,0,0,1-.39-.63v-1A2.75,2.75,0,0,1,23,66.65l2.67-2.05a4.1,4.1,0,0,1,2.55-.85h.75a1.29,1.29,0,0,1,1,.43,1.36,1.36,0,0,1,.42,1V80.57a1.39,1.39,0,0,1-.42,1,1.32,1.32,0,0,1-1,.42h-.75a1.38,1.38,0,0,1-1-.42,1.4,1.4,0,0,1-.43-1v-13l0,0,0,0Z"/>
                  </g>
                </g>
                <g id="attempt2">
                  <path id="potion-2" data-name="potion" className="cls-1" d="M28.26,111c11.35.07,8.8,3.24,8.87,3.76s-.93,4.64-1.28,6.08a.49.49,0,0,0,.35.59c.76.19,2.2.64,2.63,1.46a4.16,4.16,0,0,1-1.48,4.66S36,127.11,35.88,137s0,17.11,8.4,23.3,10.18,13.87,10.18,13.87,5.6,20.2-10.47,29.64l-.89,2.36s-3.83,3.1-13.42,3.1h-3c-9.58,0-13.42-3.1-13.42-3.1l-.88-2.36C-3.66,194.36,2,174.16,2,174.16s1.77-7.67,10.17-13.87,8.56-13.42,8.41-23.3-1.48-9.44-1.48-9.44a4.18,4.18,0,0,1-1.48-4.66c.43-.82,1.88-1.27,2.63-1.46a.48.48,0,0,0,.35-.59c-.34-1.44-1.34-5.65-1.28-6.08s-2.47-3.69,8.87-3.76Z"/>
                  <g id="number2">
                    <path className="cls-2" d="M33.26,178.82a7.84,7.84,0,0,1-1.49,4.51A33.06,33.06,0,0,1,26,189.07a.18.18,0,0,0-.05.07,0,0,0,0,0,0,0h5.9a1.38,1.38,0,0,1,1,.41,1.32,1.32,0,0,1,.42,1A1.43,1.43,0,0,1,31.89,192H22.72a1.32,1.32,0,0,1-1-.42,1.42,1.42,0,0,1-.41-1,2.78,2.78,0,0,1,1.07-2.28,37,37,0,0,0,5.8-5.37,6.21,6.21,0,0,0,1.47-3.75c0-1.85-1-2.77-3-2.77a10.43,10.43,0,0,0-3.7.85.89.89,0,0,1-.89-.08.86.86,0,0,1-.41-.75V176a2.11,2.11,0,0,1,.36-1.19,1.81,1.81,0,0,1,1-.71,15.23,15.23,0,0,1,4.17-.6,6.57,6.57,0,0,1,4.5,1.4A5,5,0,0,1,33.26,178.82Z"/>
                  </g>
                </g>
                <g id="attempt3">
                  <path id="potion-3" data-name="potion" className="cls-1" d="M28.26,221c11.35.07,8.8,3.24,8.87,3.76s-.93,4.64-1.28,6.08a.49.49,0,0,0,.35.59c.76.19,2.2.64,2.63,1.46a4.16,4.16,0,0,1-1.48,4.66S36,237.11,35.88,247s0,17.11,8.4,23.3,10.18,13.87,10.18,13.87,5.6,20.2-10.47,29.64l-.89,2.36s-3.83,3.1-13.42,3.1h-3c-9.58,0-13.42-3.1-13.42-3.1l-.88-2.36C-3.66,304.36,2,284.16,2,284.16s1.77-7.67,10.17-13.87,8.56-13.42,8.41-23.3-1.48-9.44-1.48-9.44a4.18,4.18,0,0,1-1.48-4.66c.43-.82,1.88-1.27,2.63-1.46a.48.48,0,0,0,.35-.59c-.34-1.44-1.34-5.65-1.28-6.08s-2.47-3.69,8.87-3.76Z"/>
                  <g id="number3">
                    <path className="cls-2" d="M22.69,286.57a1.38,1.38,0,0,1-1-.41,1.32,1.32,0,0,1-.42-1,1.45,1.45,0,0,1,1.42-1.43h8.87a1.33,1.33,0,0,1,1,.43,1.39,1.39,0,0,1,.41,1,3,3,0,0,1-1.05,2.34l-4.05,3.68a.08.08,0,0,0,0,.05l0,0h.4a4.56,4.56,0,0,1,4.95,5,5.63,5.63,0,0,1-1.75,4.44,7.25,7.25,0,0,1-5,1.56,15.28,15.28,0,0,1-3.72-.45,1.65,1.65,0,0,1-1-.67,2,2,0,0,1-.38-1.18v-.27a.91.91,0,0,1,.41-.79,1,1,0,0,1,.92-.14,11.57,11.57,0,0,0,3.59.68,3.73,3.73,0,0,0,2.55-.8,3,3,0,0,0,.9-2.33,2.16,2.16,0,0,0-1-2,7.35,7.35,0,0,0-3.56-.61h-.27a1.16,1.16,0,0,1-.87-.37,1.24,1.24,0,0,1-.36-.88,2.69,2.69,0,0,1,.9-2.07l4.07-3.7s0,0,0,0a0,0,0,0,0,0,0Z"/>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
};